import React from "react";
import { Box, Text, Card, Stack, TextField, Inline, Checkbox } from "braid-design-system";
import { useTranslation } from "react-i18next";
import { DAYS_OF_WEEK, daysToMask, maskToDays } from "./types";

interface PromotionHeaderSectionProps {
    promoCode: string;
    promoName: string;
    promoDesc: string;
    promoDateFrom: string;
    promoDateTo: string;
    promoTimeFrom: string;
    promoTimeTo: string;
    promoDaysMask: number;
    promoActive: boolean;
    onChange: (field: string, value: any) => void;
    isReadOnly?: boolean;
    errors?: Record<string, string>;
}

const PromotionHeaderSection: React.FC<PromotionHeaderSectionProps> = ({
    promoCode,
    promoName,
    promoDesc,
    promoDateFrom,
    promoDateTo,
    promoTimeFrom,
    promoTimeTo,
    promoDaysMask,
    promoActive,
    onChange,
    isReadOnly = false,
    errors,
}) => {
    const { t } = useTranslation();
    const selectedDays = maskToDays(promoDaysMask);

    const handleDayToggle = (dayValue: number, checked: boolean) => {
        let newDays: number[];
        if (checked) {
            newDays = [...selectedDays, dayValue];
        } else {
            newDays = selectedDays.filter((d) => d !== dayValue);
        }
        onChange('promo_days_mask', daysToMask(newDays));
    };

    const handleAllDaysToggle = (checked: boolean) => {
        onChange('promo_days_mask', checked ? daysToMask([0, 1, 2, 3, 4, 5, 6]) : 0);
    };

    const allSelected = selectedDays.length === 7;

    return (
        <Card>
            <Stack space="medium">
                <Text weight="strong" size="large">
                    {t('page.promotion.header.title', 'Promotion Details')}
                </Text>

                {/* Code and Name */}
                <Inline space="small" alignY="center">
                    <Box flexGrow={1}>
                        <TextField
                            id="promo-code"
                            label={t('page.promotion.header.code', 'Promotion Code')}
                            placeholder="e.g. SUMMER2026"
                            value={promoCode}
                            onChange={(e) => onChange('promo_code', e.currentTarget.value)}
                            disabled={isReadOnly}
                        />
                    </Box>
                    <Box flexGrow={1}>
                        <TextField
                            id="promo-name"
                            label={t('page.promotion.header.name', 'Promotion Name')}
                            placeholder="e.g. Summer Sale 2026"
                            value={promoName}
                            onChange={(e) => onChange('promo_name', e.currentTarget.value)}
                            disabled={isReadOnly}
                        />
                    </Box>
                </Inline>

                {/* Description */}
                <TextField
                    id="promo-desc"
                    label={t('page.promotion.header.description', 'Description (Optional)')}
                    placeholder="Brief description of the promotion..."
                    value={promoDesc}
                    onChange={(e) => onChange('promo_desc', e.currentTarget.value)}
                    disabled={isReadOnly}
                />

                {/* Date From / Date To */}
                <Inline space="small" alignY="center" align="left">
                    <Box>
                        <Text size="small" tone="secondary">
                            {t('page.promotion.header.date_from', 'Date From')}
                        </Text>
                        <input
                            type="date"
                            id="promo-datefrom"
                            value={promoDateFrom}
                            onChange={(e) => onChange('promo_datefrom', e.target.value)}
                            disabled={isReadOnly}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '16px',
                                border: errors?.promo_datefrom ? '2px solid #dc2626' : '1px solid #ccc',
                                borderRadius: '6px',
                                marginTop: '4px',
                            }}
                        />
                        {errors?.promo_datefrom && (
                            <Text size="xsmall" tone="critical">{errors.promo_datefrom}</Text>
                        )}
                    </Box>
                    <Box>
                        <Text size="small" tone="secondary">
                            {t('page.promotion.header.date_to', 'Date To')}
                        </Text>
                        <input
                            type="date"
                            id="promo-dateto"
                            value={promoDateTo}
                            onChange={(e) => onChange('promo_dateto', e.target.value)}
                            disabled={isReadOnly}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '16px',
                                border: errors?.promo_dateto ? '2px solid #dc2626' : '1px solid #ccc',
                                borderRadius: '6px',
                                marginTop: '4px',
                            }}
                        />
                        {errors?.promo_dateto && (
                            <Text size="xsmall" tone="critical">{errors.promo_dateto}</Text>
                        )}
                    </Box>
                </Inline>

                {/* Time From / Time To */}
                <Inline space="small" alignY="center" align="left">
                    <Box>
                        <Text size="small" tone="secondary">
                            {t('page.promotion.header.time_from', 'Time From')}
                        </Text>
                        <input
                            type="time"
                            id="promo-timefrom"
                            value={promoTimeFrom}
                            onChange={(e) => onChange('promo_timefrom', e.target.value)}
                            disabled={isReadOnly}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '16px',
                                border: errors?.promo_timefrom ? '2px solid #dc2626' : '1px solid #ccc',
                                borderRadius: '6px',
                                marginTop: '4px',
                            }}
                        />
                        {errors?.promo_timefrom && (
                            <Text size="xsmall" tone="critical">{errors.promo_timefrom}</Text>
                        )}
                    </Box>
                    <Box>
                        <Text size="small" tone="secondary">
                            {t('page.promotion.header.time_to', 'Time To')}
                        </Text>
                        <input
                            type="time"
                            id="promo-timeto"
                            value={promoTimeTo}
                            onChange={(e) => onChange('promo_timeto', e.target.value)}
                            disabled={isReadOnly}
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                fontSize: '16px',
                                border: errors?.promo_timeto ? '2px solid #dc2626' : '1px solid #ccc',
                                borderRadius: '6px',
                                marginTop: '4px',
                            }}
                        />
                        {errors?.promo_timeto && (
                            <Text size="xsmall" tone="critical">{errors.promo_timeto}</Text>
                        )}
                    </Box>
                </Inline>

                {/* Days of Week */}
                <Stack space="xsmall">
                    <Text weight="strong">{t('page.promotion.header.days', 'Days of Week')}</Text>
                    <Inline space="small" alignY="center">
                        <Checkbox
                            id="day-all"
                            label={t('page.promotion.header.all_days', 'All')}
                            checked={allSelected}
                            onChange={(e) => handleAllDaysToggle(e.currentTarget.checked)}
                            disabled={isReadOnly}
                        />
                        {DAYS_OF_WEEK.map((day) => (
                            <Checkbox
                                key={day.value}
                                id={`day-${day.value}`}
                                label={day.label}
                                checked={selectedDays.includes(day.value)}
                                onChange={(e) => handleDayToggle(day.value, e.currentTarget.checked)}
                                disabled={isReadOnly || allSelected}
                            />
                        ))}
                    </Inline>
                </Stack>

                {/* Active */}
                <Checkbox
                    id="promo-active"
                    label={t('page.promotion.header.active', 'Active')}
                    checked={promoActive}
                    onChange={(e) => onChange('promo_active', e.currentTarget.checked)}
                    disabled={isReadOnly}
                />
            </Stack>
        </Card>
    );
};

export default PromotionHeaderSection;